import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MapOutlined, NavigationOutlined, ShareOutlined } from '@mui/icons-material';
import { Popover, Paper, MenuList, MenuItem, ListItemText, ListItemIcon, Drawer, List, ListItem } from '@mui/material';

import { ctxPluto } from '@/contexts/PlutoContext';
import { ctxUser } from '@/contexts/UserContext';
import { isValidLatLong } from '@/utilities/latlong';
import { openLocation, routeLocation } from '@/utilities/externalMapUtils';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { TargetNameAndDescription, targetNameAndDescriptionStyle } from '@/components/common/TargetNameAndDescription';
import { MinimalTarget } from '@/generated/model';

import './MenuPopover.css';

export function MenuPopover({
  element,
  target,
  onClose,
}: {
  element: Element | null;
  target: MinimalTarget;
  onClose: (full: boolean) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ctx = useContext(ctxPluto);
  const userCtx = useContext(ctxUser);
  const [isDrawerOpen, setDrawerOpen] = useState<boolean>(true);
  const [isVisible, setVisible] = useState(false);

  useEffect(() => {
    if (isDrawerOpen) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isDrawerOpen]);

  function findTarget(id: number) {
    if (!ctx || !ctx.fullTargets) return undefined;

    return [...ctx.fullTargets].find((t) => t.target.id === id);
  }

  function onOpenExternalMap() {
    const t = findTarget(target.id);
    if (!t || !t.currentPosition || !isValidLatLong(t.currentPosition)) {
      return;
    }
    openLocation({ lat: t.currentPosition.latitude, lon: t.currentPosition.longitude, title: t.target.name });
    onClose(false);
  }

  function onRouteExternalMap() {
    const t = findTarget(target.id);
    if (!t || !t.currentPosition || !isValidLatLong(t.currentPosition)) {
      return;
    }
    routeLocation({ lat: t.currentPosition.latitude, lon: t.currentPosition.longitude, title: t.target.name });
    onClose(false);
  }

  function shareTarget() {
    navigate('./share?targetId=' + target.id + '&targetName=' + target.name);
    onClose(false);
  }

  const menuList = (size: 'medium' | 'small') => [
    {
      key: 'tracking-menu-popover-open-external-map',
      content: (
        <>
          <ListItemIcon>
            <MapOutlined fontSize={size} />
          </ListItemIcon>
          <ListItemText>{t('targetMenu.locationOnMap')}</ListItemText>
        </>
      ),
      onClick: onOpenExternalMap,
      canAppearWhenToken: true,
    },
    {
      key: 'tracking-menu-popover-route-external-map',
      content: (
        <>
          <ListItemIcon>
            <NavigationOutlined fontSize={size} />
          </ListItemIcon>
          <ListItemText>{t('targetMenu.navigateOnMap')}</ListItemText>
        </>
      ),
      onClick: onRouteExternalMap,
      canAppearWhenToken: true,
    },
    {
      key: 'tracking-menu-popover-share-target',
      content: (
        <>
          <ListItemIcon>
            <ShareOutlined fontSize={size} />
          </ListItemIcon>
          <ListItemText>{t('targetMenu.sharableLink')}</ListItemText>
        </>
      ),
      onClick: shareTarget,
      canAppearWhenToken: false,
      requiredExtraFeature: 'extrafeature_pluto_target_share',
    },
  ];

  return isPhoneOrHighScreen() ? (
    <Drawer
      anchor="bottom"
      open={isVisible}
      className="tracking-popover-menu"
      onClose={() => {
        setDrawerOpen(false);
        setTimeout(() => onClose(false), 300);
      }}
      ModalProps={{ keepMounted: true }}
      slotProps={{
        paper: {
          className: 'tracking-popover-menu-paper',
          sx: {
            transform: isDrawerOpen ? 'translateY(0%)' : 'translateY(100%)',
            transition: 'transform 0.3s ease',
          },
        },
      }}
    >
      <div className="tracking-popover-menu-header">
        <TargetNameAndDescription
          targetName={target.name}
          targetDescription={target.description}
          styleClassName={targetNameAndDescriptionStyle(undefined, undefined)}
        />
      </div>
      <List>
        {menuList('medium')
          .filter((item) => {
            const requiredExtraFeature = item.requiredExtraFeature;
            if (!requiredExtraFeature) {
              return true;
            }
            return userCtx?.hasExtraFeature(requiredExtraFeature);
          })
          .filter((item) => item.canAppearWhenToken || !userCtx?.isToken())
          .map((listItem) => (
            <ListItem key={listItem.key} onClick={listItem.onClick} data-qa-element-id={listItem.key}>
              {listItem.content}
            </ListItem>
          ))}
      </List>
    </Drawer>
  ) : (
    <Popover
      open={true}
      anchorEl={element}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      onClose={() => onClose(false)}
    >
      <Paper className="menu-popover-paper">
        <MenuList>
          {menuList('small')
            .filter((item) => {
              const requiredExtraFeature = item.requiredExtraFeature;
              if (!requiredExtraFeature) {
                return true;
              }
              return userCtx?.hasExtraFeature(requiredExtraFeature);
            })
            .filter((item) => item.canAppearWhenToken || !userCtx?.isToken())
            .map((listItem) => (
              <MenuItem key={listItem.key} onClick={listItem.onClick} data-qa-element-id={listItem.key}>
                {listItem.content}
              </MenuItem>
            ))}
        </MenuList>
      </Paper>
    </Popover>
  );
}
